import { useArtifactPanelStore } from '@/stores/artifactPanelStore.js';
import { useAppContext } from '@/contexts/AppContext.js';
import { Tab } from './Tab.js';
import { Plus, MoreHorizontal, X, PanelLeft, PanelRight } from 'lucide-react';
import { Button } from '@/components/ui/button.js';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu.js';

export function TabBar() {
  const openTabs = useArtifactPanelStore((state) => state.openTabs);
  const artifacts = useArtifactPanelStore((state) => state.artifacts);
  const activeTabId = useArtifactPanelStore((state) => state.activeTabId);
  const setActiveTab = useArtifactPanelStore((state) => state.setActiveTab);
  const closeTab = useArtifactPanelStore((state) => state.closeTab);
  const closeAllTabs = useArtifactPanelStore((state) => state.closeAllTabs);
  const closeOtherTabs = useArtifactPanelStore((state) => state.closeOtherTabs);
  const registerArtifact = useArtifactPanelStore((state) => state.registerArtifact);
  const openTab = useArtifactPanelStore((state) => state.openTab);
  const openBrowser = useArtifactPanelStore((state) => state.openBrowser);
  const closePanel = useArtifactPanelStore((state) => state.closePanel);
  const layoutOrientation = useArtifactPanelStore((state) => state.layoutOrientation);
  const toggleLayoutOrientation = useArtifactPanelStore((state) => state.toggleLayoutOrientation);

  const { createNewNote, currentNote } = useAppContext();

  const handleNewAction = (action: 'note' | 'browser') => {
    if (action === 'note') {
      // Create note in AppContext
      createNewNote();

      // Wait for state update and then open as artifact
      setTimeout(() => {
        if (currentNote) {
          const artifactId = `note-${currentNote.id}`;

          registerArtifact({
            id: artifactId,
            type: 'note',
            title: currentNote.title || 'Untitled Note',
            isPinned: false,
            lastOpened: Date.now(),
            createdAt: currentNote.createdAt,
            modifiedAt: currentNote.updatedAt,
            data: { noteId: currentNote.id },
          });

          openTab({
            id: artifactId,
            type: 'note',
            title: currentNote.title || 'Untitled Note',
            data: { noteId: currentNote.id },
          });
        }
      }, 0);
    } else if (action === 'browser') {
      openBrowser('', 'Browser');
    }
  };

  return (
    <>
      {/* Tabs */}
      <div className="flex-1 flex items-center gap-1 min-w-0 overflow-x-auto scrollbar-thin">
        {openTabs.map((tabId) => {
          const artifact = artifacts[tabId];
          if (!artifact) return null;

          return (
            <Tab
              key={tabId}
              artifact={artifact}
              isActive={activeTabId === tabId}
              onClick={() => setActiveTab(tabId)}
              onClose={() => closeTab(tabId)}
              onCloseOthers={() => closeOtherTabs(tabId)}
            />
          );
        })}
      </div>

      {/* Actions */}
      <div className="flex items-center gap-1 flex-shrink-0">
        {/* New Tab */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="h-7 w-7">
              <Plus className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => handleNewAction('note')}>
              New Note
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => handleNewAction('browser')}>
              New Browser Tab
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        {/* More Options */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="h-7 w-7">
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem
              onClick={closeAllTabs}
              disabled={openTabs.length === 0}
            >
              Close All Tabs
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => activeTabId && closeOtherTabs(activeTabId)}
              disabled={openTabs.length <= 1}
            >
              Close Other Tabs
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={toggleLayoutOrientation}>
              <div className="flex items-center gap-2">
                {layoutOrientation === 'left' ? (
                  <PanelLeft className="h-4 w-4" />
                ) : (
                  <PanelRight className="h-4 w-4" />
                )}
                <span>Move to {layoutOrientation === 'left' ? 'Right' : 'Left'}</span>
              </div>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={closePanel}>
              Close Panel
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Close Panel */}
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7"
          onClick={closePanel}
        >
          <X className="h-4 w-4" />
        </Button>
      </div>
    </>
  );
}
